/**
 * @file Onboarding LIST page (`#/onboarding`) — admin.
 *
 * Queue view of joining employees (phase == 'onboarding'), plus the
 * "add a manual joiner" modal. The per-employee onboarding workflow
 * (cycling each item through its status state machine, capturing the SN
 * at the "prepared" step) lives on /people/:id — main.js redirects
 * `#/onboarding/:id` there, so there is one onboarding work surface.
 *
 * Data: employees where phase == 'onboarding', joined with
 * onboardings/{employeeId}.items for the per-person progress counts.
 */

import { el, clear, formatDate, toast } from '../utils.js';
import { Icons, categoryIcon }          from '../icons.js';
import { CATEGORIES, ONBOARDING_STATUS, PHASES } from '../config.js';
import { navigate }                     from '../router.js';
import { watchEmployees, createEmployee, getAllOnboardings } from '../db.js';
import { renderPageHead } from '../page-header.js';
import { sortableTable, listCard } from '../lib/sortable-table.js';
import { loadListState, saveListState, setListContext } from '../lib/list-memory.js';
import { overdueDateCell } from '../lib/overdue.js';
import { renderLoadError } from '../lib/load-error.js';
import { copyable } from '../lib/copyable.js';

// ── List page ──────────────────────────────────────────────────────────────
export function renderOnboardingList(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'Onboarding',
    'New joiners and the devices they need handed over.',
  ));

  // Filter / view / category / sort are remembered per browser tab (see
  // lib/list-memory.js) so coming back from a person lands on the same list.
  const state = {
    ...loadListState('onboarding', {
      filter:   'open',   // 'open' | 'all'
      view:     'person', // 'person' | 'category'
      category: 'all',    // category key or 'all' — only used in 'category' view
      personSort:   { index: 2, dir: 'asc' }, // soonest start first
      categorySort: { index: 3, dir: 'asc' }, // soonest start first
    }),
    employees: [],
    onboardings: new Map(),
  };
  const remember = (patch) => saveListState('onboarding', patch);

  const segActive = el('button', { class: state.filter === 'open' ? 'active' : null }, 'Needs action');
  const segAll    = el('button', { class: state.filter === 'all' ? 'active' : null }, 'All in phase');
  segActive.addEventListener('click', () => { state.filter = 'open'; remember({ filter: 'open' }); segActive.classList.add('active'); segAll.classList.remove('active'); rerender(); });
  segAll.addEventListener('click',    () => { state.filter = 'all';  remember({ filter: 'all' });  segAll.classList.add('active');    segActive.classList.remove('active'); rerender(); });

  const segPerson   = el('button', { class: state.view === 'person' ? 'active' : null }, 'By person');
  const segCategory = el('button', { class: state.view === 'category' ? 'active' : null }, 'By category');
  segPerson.addEventListener('click',   () => { state.view = 'person';   remember({ view: 'person' });   segPerson.classList.add('active');   segCategory.classList.remove('active'); rerender(); });
  segCategory.addEventListener('click', () => { state.view = 'category'; remember({ view: 'category' }); segCategory.classList.add('active'); segPerson.classList.remove('active');   rerender(); });

  const categorySelect = el('select', { style: 'margin-left:8px' },
    el('option', { value: 'all' }, 'All categories'),
    ...Object.entries(CATEGORIES).map(([key, def]) => el('option', { value: key }, def.label)),
  );
  categorySelect.value = state.category;
  categorySelect.addEventListener('change', () => { state.category = categorySelect.value; remember({ category: state.category }); rerender(); });

  const toolbar = el('div', { class: 'toolbar' },
    el('div', { class: 'row' },
      el('div', { class: 'segment' }, segActive, segAll),
      el('div', { class: 'segment' }, segPerson, segCategory),
      categorySelect,
    ),
    el('div', { class: 'row' },
      el('button', {
        class: 'btn',
        onClick: () => openAddManualModal(),
      }, Icons.plus(), 'Add manually'),
    ),
  );
  host.appendChild(toolbar);

  const tableHost = el('div', { class: 'card' });
  host.appendChild(tableHost);

  function rerender() {
    clear(tableHost);
    // Show the category dropdown only when in category view.
    categorySelect.style.display = state.view === 'category' ? '' : 'none';
    if (state.view === 'person') {
      renderPersonView();
    } else {
      renderCategoryView();
    }
  }

  function renderPersonView() {
    const rows = state.employees.map((e) => {
      const ob = state.onboardings.get(e.id);
      const items = ob?.items || [];
      const pending = items.filter((it) => !['handed_over', 'cancelled'].includes(it.status)).length;
      const done = items.length - pending;
      return { ...e, items, pending, done };
    });
    const visible = state.filter === 'open' ? rows.filter((r) => r.items.length === 0 || r.pending > 0) : rows;
    if (!visible.length) {
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.user(),
        el('p', null, state.filter === 'open' ? 'Nothing needs action right now.' : 'No active onboardings.'),
        el('p', { class: 'muted' }, state.filter === 'open'
          ? 'Switch to "All in phase" to see joiners already fully processed.'
          : 'Pull the latest from Bamboo or add someone manually.'),
      ));
      return;
    }
    const progressNode = (r) => (r.items.length === 0
      ? el('span', { class: 'muted' }, 'No form response yet')
      : (r.pending === 0
        ? el('span', { class: 'badge badge-done' }, 'All done')
        : el('span', null, `${r.done}/${r.items.length} done`)));
    // Overdue = already started AND still has open items (or never
    // responded) — the rows that need eyes first.
    const startCell = (r) => overdueDateCell(r.startDate, r.items.length === 0 || r.pending > 0, 'started');
    const renderEmployeeRow = (r) =>
      el('tr', { onClick: () => navigate(`/people/${r.id}`), 'data-row-link': true },
        el('td', null, r.fullName || '—'),
        el('td', { class: 'muted' }, r.email ? copyable(r.email, { label: 'email' }) : '—'),
        el('td', null, startCell(r)),
        el('td', null, progressNode(r)),
      );
    const renderEmployeeCard = (r) => listCard({
      title: r.fullName || '—',
      meta:  [r.email, startCell(r)],
      aside: [progressNode(r)],
      onClick: () => navigate(`/people/${r.id}`),
    });
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Employee',   sortValue: (r) => r.fullName },
        { label: 'Email',      sortValue: (r) => r.email },
        { label: 'Start date', sortValue: (r) => r.startDate },
        // Progress as a fraction; "no form response yet" sorts before 0% done.
        { label: 'Progress',   sortValue: (r) => (r.items.length === 0 ? -1 : r.done / r.items.length) },
      ],
      rows: visible,
      renderRow: renderEmployeeRow,
      renderCard: renderEmployeeCard,
      initialSort: state.personSort,
      onOrder: ({ rows: ordered, sortIndex, sortDir }) => {
        state.personSort = { index: sortIndex, dir: sortDir };
        remember({ personSort: state.personSort });
        setListContext({ source: 'onboarding', ids: ordered.map((r) => r.id), href: '#/onboarding', label: 'Onboarding' });
      },
    }));
  }

  function renderCategoryView() {
    // Flatten all onboarding items across employees + attach employee context.
    const empById = new Map(state.employees.map((e) => [e.id, e]));
    const tasks = [];
    for (const [employeeId, ob] of state.onboardings.entries()) {
      const employee = empById.get(employeeId);
      if (!employee) continue;
      for (const item of (ob.items || [])) {
        if (state.category !== 'all' && item.category !== state.category) continue;
        if (state.filter === 'open' && ['handed_over', 'cancelled'].includes(item.status)) continue;
        tasks.push({ employee, item });
      }
    }
    // Pre-sort by employee name so the table's stable sort keeps name as the
    // tiebreaker; the Start column is the initial (primary) sort key below.
    tasks.sort((a, b) => (a.employee.fullName || '').localeCompare(b.employee.fullName || ''));
    if (!tasks.length) {
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.inbox(),
        el('p', null, state.filter === 'open' ? 'Nothing pending in this category.' : 'No items in this category.'),
        el('p', { class: 'muted' }, state.category !== 'all'
          ? 'Different category? Switch with the dropdown above.'
          : 'No items at all — pull the latest from BambooHR or Google Form.'),
      ));
      return;
    }
    const renderTaskRow = ({ employee, item }) => {
      const cat    = CATEGORIES[item.category] || { label: item.category };
      const status = ONBOARDING_STATUS[item.status] || ONBOARDING_STATUS.requested;
      return el('tr', {
        onClick: () => navigate(`/people/${employee.id}`),
        style:   'cursor:pointer',
        'data-row-link': true,
      },
        el('td', null, el('span', { style: 'display:inline-flex;align-items:center;gap:6px' },
          categoryIcon(item.category), cat.label)),
        el('td', null, item.name || el('span', { class: 'muted' }, cat.label)),
        el('td', null, employee.fullName || '—'),
        el('td', { class: 'muted' }, formatDate(employee.startDate)),
        el('td', null, el('span', { class: `badge badge-${status.badge}` }, status.label)),
      );
    };
    const renderTaskCard = ({ employee, item }) => {
      const cat    = CATEGORIES[item.category] || { label: item.category };
      const status = ONBOARDING_STATUS[item.status] || ONBOARDING_STATUS.requested;
      return listCard({
        title: item.name || cat.label,
        meta:  [cat.label, employee.fullName, formatDate(employee.startDate)],
        aside: [el('span', { class: `badge badge-${status.badge}` }, status.label)],
        onClick: () => navigate(`/people/${employee.id}`),
      });
    };
    // Status sorts by lifecycle order (Requested → … → Cancelled), not alpha.
    const statusOrder = Object.keys(ONBOARDING_STATUS);
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Category', sortValue: ({ item }) => (CATEGORIES[item.category]?.label || item.category) },
        { label: 'Item',     sortValue: ({ item }) => (item.name || CATEGORIES[item.category]?.label || item.category) },
        { label: 'For',      sortValue: ({ employee }) => employee.fullName },
        { label: 'Start',    sortValue: ({ employee }) => employee.startDate },
        { label: 'Status',   sortValue: ({ item }) => statusOrder.indexOf(item.status) },
      ],
      rows: tasks,
      renderRow: renderTaskRow,
      renderCard: renderTaskCard,
      initialSort: state.categorySort,
      onOrder: ({ rows: ordered, sortIndex, sortDir }) => {
        state.categorySort = { index: sortIndex, dir: sortDir };
        remember({ categorySort: state.categorySort });
        // One id per person, in first-appearance order of their items.
        setListContext({ source: 'onboarding', ids: ordered.map(({ employee }) => employee.id), href: '#/onboarding', label: 'Onboarding' });
      },
    }));
  }

  // Live subscription — re-render on Firestore updates.
  const unsub = watchEmployees(PHASES.onboarding, async (employees) => {
    state.employees = employees;
    // Fetch onboarding docs for every employee in the list, in parallel.
    const obs = await getAllOnboardings();
    state.onboardings = new Map(obs.map((o) => [o.id, o]));
    rerender();
  }, () => {
    clear(tableHost);
    tableHost.appendChild(renderLoadError('Could not load the onboarding queue.'));
  });

  // When the page is unmounted (route changes), clean up.
  host.addEventListener('cleanup', unsub, { once: true });
}

function openAddManualModal() {
  const backdrop = el('div', { class: 'modal-backdrop open' });
  const fields = {
    fullName: el('input', { type: 'text', required: true }),
    email:    el('input', { type: 'email' }),
    startDate: el('input', { type: 'date' }),
    bambooId: el('input', { type: 'text', placeholder: 'optional — used for later merge' }),
  };

  const close = () => backdrop.remove();
  const submit = async () => {
    const fullName = fields.fullName.value.trim();
    if (!fullName) {
      toast('Full name is required.', 'error');
      return;
    }
    try {
      const id = await createEmployee({
        fullName,
        email:     fields.email.value.trim() || null,
        startDate: fields.startDate.value || null,
        bambooId:  fields.bambooId.value.trim() || null,
        phase:     PHASES.onboarding,
        source:    'manual',
      });
      toast('Employee added.', 'success');
      close();
      navigate(`/people/${id}`);
    } catch (err) {
      console.error(err);
      toast(err.message || 'Could not save.', 'error');
    }
  };

  backdrop.appendChild(el('div', { class: 'modal' },
    el('h2', null, 'Add employee manually'),
    el('p', { class: 'muted', style: 'margin-top:0' },
      'Use this for special cases. If they later appear in BambooHR with the same Bamboo ID, the records will merge automatically.'),
    el('div', { class: 'stack', style: 'margin-top:16px' },
      el('div', { class: 'field' }, el('label', null, 'Full name'), fields.fullName),
      el('div', { class: 'field' }, el('label', null, 'Email'),     fields.email),
      el('div', { class: 'field' }, el('label', null, 'Start date'), fields.startDate),
      el('div', { class: 'field' }, el('label', null, 'Bamboo ID (optional)'), fields.bambooId),
    ),
    el('div', { class: 'row', style: 'margin-top:24px;justify-content:flex-end' },
      el('button', { class: 'btn-ghost btn', onClick: close }, 'Cancel'),
      el('button', { class: 'btn',           onClick: submit }, 'Add'),
    ),
  ));
  document.body.appendChild(backdrop);
  fields.fullName.focus();
}
